import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const KEY = "gemPriceSubscription";
const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const [localSubscribed, setLocalSubscribed] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj?.active) setLocalSubscribed(true);
      }
    } catch (_) {}
  }, []);

  // Auto-sync localStorage when the server tells us the account is subscribed.
  // Covers Google login, email login and admin-granted subscriptions — the
  // frontend no longer needs a Stripe redirect to unlock the pricing grids.
  useEffect(() => {
    if (user?.subscription_active && !localSubscribed) {
      const payload = {
        active: true,
        session_id: user.subscription_session_id || null,
        activated_at: new Date().toISOString(),
      };
      localStorage.setItem(KEY, JSON.stringify(payload));
      setLocalSubscribed(true);
    }
  }, [user, localSubscribed]);

  const subscribed = localSubscribed || Boolean(user?.subscription_active);

  const activate = useCallback((sessionId) => {
    const payload = { active: true, session_id: sessionId, activated_at: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(payload));
    setLocalSubscribed(true);
  }, []);

  const clear = useCallback(() => {
    localStorage.removeItem(KEY);
    setLocalSubscribed(false);
  }, []);

  return (
    <SubscriptionContext.Provider value={{ subscribed, activate, clear }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be within SubscriptionProvider");
  return ctx;
};
